import {
  getScoringStudentName,
  isSyntheticScanAnswerText,
  resolveAnswerTextForResult,
} from './scoringResultMeta';
import {
  getGradeOnlySummary,
  isGradeOnlyResult,
} from './scoringGradeMode';
import {
  formatRecognitionSourcePages,
  splitTextForExcel,
} from '../../utils/recognitionReview';

export const getScoringResultTotalForExport = (result) => {
  if (Array.isArray(result?.question_results) && result.question_results.length > 0) {
    return result.question_results.reduce((sum, item) => sum + (Number(item?.score) || 0), 0);
  }
  if (Array.isArray(result?.item_scores) && result.item_scores.length > 0) {
    return result.item_scores.reduce((sum, item) => sum + (Number(item?.score) || 0), 0);
  }
  return result?.total_score ?? result?.score ?? 0;
};

const getExportableAnswerText = (answer) => (
  isSyntheticScanAnswerText(answer?.answer_text) ? '' : (answer?.answer_text || '')
);

const appendScoreColumns = (row, result, rubric) => {
  const gradeOnly = isGradeOnlyResult(result, rubric);
  const nextRow = {
    ...row,
    [gradeOnly ? '종합 수행수준' : '총점']: gradeOnly
      ? getGradeOnlySummary(result)
      : getScoringResultTotalForExport(result),
  };

  (result?.question_results || []).forEach((question) => {
    const title = question.question_title || `${question.question_id}번 문항`;
    const label = gradeOnly ? `${title} 수행수준` : `${title} (${question.max_score}점)`;
    nextRow[label] = gradeOnly
      ? (question.matched_level || getGradeOnlySummary(question))
      : question.score;
  });

  (result?.item_scores || []).forEach((item) => {
    const label = gradeOnly
      ? `${item.item_name} 수행수준`
      : `${item.item_name} (${item.max_score}점)`;
    nextRow[label] = gradeOnly ? (item.matched_level || '') : item.score;
  });

  if (result?.strengths?.length > 0) nextRow['잘한 점'] = result.strengths.join(', ');
  if (result?.improvements?.length > 0) nextRow['보완할 점'] = result.improvements.join(', ');
  nextRow['종합 의견'] = result?.detailed_feedback || '';
  nextRow['교과세특 생기부'] = result?.teacher_comment || '';
  if (result?.error) nextRow['오류'] = result.error;

  return nextRow;
};

export const buildScoringResultExportRows = ({
  results = [],
  cachedAnswerTexts = [],
  batchStudents = [],
  rubric = null,
} = {}) => (
  (Array.isArray(results) ? results : []).map((result, index) => {
    const answer = resolveAnswerTextForResult({
      cachedAnswerTexts,
      results,
      result,
      resultIndex: index,
      batchStudents,
    });
    const studentName = getScoringStudentName(result, answer.student_name || `학생 ${index + 1}`);
    const fileName = result?.fileName
      || result?.filename
      || answer?.fileName
      || answer?.filename
      || '';
    const pages = answer?.source_pages?.length > 0
      ? answer.source_pages
      : (
        answer?.pages
        || result?.pages
        || result?.source_pages
        || ''
      );

    return appendScoreColumns({
      '번호': index + 1,
      '학생명': studentName,
      '파일명': fileName,
      '원본 쪽': formatRecognitionSourcePages(pages),
      ...splitTextForExcel(getExportableAnswerText(answer), 'AI가 읽은 답안 원문'),
    }, result, rubric);
  })
);

const buildHistoryScoreText = (result, rubric) => {
  const gradeOnly = isGradeOnlyResult(result, rubric);
  const questionScoreText = (result?.question_results || [])
    .map(question => gradeOnly
      ? `${question.question_title || `${question.question_id}번 문항`}: ${question.matched_level || getGradeOnlySummary(question)}`
      : `${question.question_title || `${question.question_id}번 문항`}: ${question.score}/${question.max_score}점`)
    .join('\n');
  const itemScoreText = (result?.item_scores || [])
    .map(item => gradeOnly
      ? `${item.item_name}: ${item.matched_level || '등급'}`
      : `${item.item_name}: ${item.score}/${item.max_score}점`)
    .join('\n');

  return { gradeOnly, questionScoreText, itemScoreText };
};

export const buildScoringHistoryExportRows = (details = []) => {
  const rows = [];

  (Array.isArray(details) ? details : []).forEach((detail) => {
    if (!detail) return;
    const results = Array.isArray(detail.results) ? detail.results : [];

    results.forEach((result, index) => {
      const answer = resolveAnswerTextForResult({
        cachedAnswerTexts: detail.cached_answer_texts || [],
        results,
        result,
        resultIndex: index,
      });
      const { gradeOnly, questionScoreText, itemScoreText } = buildHistoryScoreText(
        result,
        detail.parsed_rubric,
      );
      const pages = answer?.source_pages?.length > 0
        ? answer.source_pages
        : (answer?.pages || result?.pages || result?.source_pages || '');

      rows.push({
        '학생명': getScoringStudentName(result, answer.student_name || `학생 ${index + 1}`),
        '채점일': new Date(detail.created_at).toLocaleDateString('ko-KR'),
        '채점 기록': detail.title || detail.subject || '',
        '파일명': result.fileName || result.filename || answer.fileName || answer.filename || '',
        '원본 쪽': formatRecognitionSourcePages(pages),
        ...splitTextForExcel(getExportableAnswerText(answer), 'AI가 읽은 답안 원문'),
        [gradeOnly ? '종합 수행수준' : '총점']: gradeOnly
          ? getGradeOnlySummary(result)
          : getScoringResultTotalForExport(result),
        [gradeOnly ? '문항별 수행수준' : '문항별 점수']: questionScoreText,
        [gradeOnly ? '항목별 수행수준' : '항목별 점수']: itemScoreText,
        '종합 의견': result.detailed_feedback || '',
        '교과세특': result.teacher_comment || '',
      });
    });
  });

  return rows;
};
